import React, { useState, useEffect, useRef } from 'react';

interface SearchableSelectProps {
    options: string[];
    value: string;
    onChange: (value: string) => void;
    id?: string;
    name?: string;
    className?: string;
    required?: boolean;
}

const SearchableSelect: React.FC<SearchableSelectProps> = ({
    options,
    value,
    onChange,
    id,
    name,
    className,
    required,
}) => {
    const [searchTerm, setSearchTerm] = useState('');
    const [isOpen, setIsOpen] = useState(false);
    const [filteredOptions, setFilteredOptions] = useState(options);
    const wrapperRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        setFilteredOptions(
            options.filter(option =>
                option.toLowerCase().includes(searchTerm.toLowerCase())
            )
        );
    }, [searchTerm, options]);

    useEffect(() => {
        function handleClickOutside(event: MouseEvent) {
            if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        }
        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [wrapperRef]);

    const handleSelect = (option: string) => {
        onChange(option);
        setSearchTerm('');
        setIsOpen(false);
    };
    
    const displayValue = searchTerm || value;

    return (
        <div className="relative" ref={wrapperRef}>
            <input
                type="text"
                id={id}
                name={name}
                value={displayValue}
                onChange={(e) => {
                    setSearchTerm(e.target.value);
                    if (e.target.value !== value) {
                        onChange('');
                    }
                }}
                onFocus={() => setIsOpen(true)}
                className={className}
                placeholder="Standort suchen oder auswählen"
                required={required}
                autoComplete="off"
            />
            {isOpen && (
                <ul className="absolute z-10 w-full bg-white border border-gray-300 rounded-md mt-1 max-h-60 overflow-y-auto">
                    {filteredOptions.length > 0 ? (
                        filteredOptions.map(option => (
                            <li
                                key={option}
                                className="px-3 py-2 cursor-pointer hover:bg-gray-100"
                                onClick={() => handleSelect(option)}
                            >
                                {option}
                            </li>
                        ))
                    ) : (
                        <li className="px-3 py-2 text-gray-500">Keine Ergebnisse</li>
                    )}
                </ul>
            )}
        </div>
    );
};

export default SearchableSelect;
